import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

/**
 * @constant {number} EVENT_CUTOFF_SECONDS - how far into a period a start event
 * still counts as "just started", and how close to the next period's start an
 * end-of-period event stops being worth showing.
 */
const EVENT_CUTOFF_SECONDS = 15;
const CLEAR_AFTER_MS = 4000;
const MS_PER_SECOND = 1000;

/**
 * Whether an end-of-period event still has time to run before the next period
 * starts, so it doesn't reappear as the break is ending.
 *
 * @param {object} period - The latest period from the match clock.
 * @returns {boolean} True while the break has time left.
 */
function isBreakStillRunning(period) {
    if (!period.nextPeriodStart) {
        return true;
    }

    return (
        Date.now() <
        Date.parse(period.nextPeriodStart) -
            MS_PER_SECOND * EVENT_CUTOFF_SECONDS
    );
}

/**
 * Detects the start and end of a period so the status badge can animate the
 * transition, e.g. "Start of 2nd Quarter" / "Half Time". The period sibling to
 * useScoreUpdate's goal/behind detection.
 *
 * @param {string} matchId - The providerId/matchId to watch the clock of.
 * @returns {object} { show, period, phase: 'start'|'end' }
 */
export function usePeriodEvent(matchId) {
    const [event, setEvent] = useState({ show: false });
    const lastFired = useRef(null);

    const matchClock = useSelector((state) =>
        PULSE.app.redux.selectors.match.matchClock(state, matchId)
    );
    const isActuallyLive = useSelector((state) =>
        PULSE.app.redux.selectors.match.isActuallyLive(state, matchId)
    );

    const period = matchClock?.periods?.[0];
    let phase = null;

    if (isActuallyLive && period) {
        if (period.periodCompleted && isBreakStillRunning(period)) {
            phase = 'end';
        } else if (
            !period.periodCompleted &&
            period.periodSeconds <= EVENT_CUTOFF_SECONDS
        ) {
            phase = 'start';
        }
    }

    const eventKey = phase ? `${period.periodNumber}-${phase}` : null;

    useEffect(() => {
        let timeout;

        if (eventKey && eventKey !== lastFired.current) {
            lastFired.current = eventKey;
            setEvent({ show: true, period, phase });
            timeout = setTimeout(
                () => setEvent({ show: false }),
                CLEAR_AFTER_MS
            );
        }

        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventKey]);

    return event;
}
